/* eslint react/prop-types: 0 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  PenLine, Loader2, AlertCircle, X, ShieldCheck,
} from 'lucide-react';
import { pickFontForName, isRTLFont, backendFontName } from './signatureFonts';

// Note: @font-face declarations live in /public/fonts/signatureFonts.css,
// linked from public/index.html so webpack's css-loader never tries to
// resolve the .ttf URLs through the module system. The fonts are loaded
// at app boot and available globally to this component.

/**
 * SignModal — typed-name-in-font digital signature capture.
 *
 *  • Name field is pre-filled (editable) from doc.your_approval.name.
 *  • Title is optional free-text (Stage 1: NOT a contacts.role_label change).
 *  • Live cursive preview uses the EXACT 3-tier font chain as the PDF
 *    (see signatureFonts.js — Great Vibes → DejaVu Italic → Noto Devanagari /
 *    Noto Naskh Arabic). Whole-name swap; never per-character mixing.
 *  • Consent checkbox is REQUIRED — submit stays disabled until ticked AND
 *    the name field is non-empty.
 *  • There is NO "approve without signing" button. A document is either
 *    all-digital or all-physical, never half-half (see locked design).
 *
 * Props:
 *   open            — bool
 *   onClose         — () => void
 *   onSigned        — async () => void   (called after a successful POST)
 *   signEndpoint    — full URL e.g. `${API}/vdr/{cid}/documents/{did}/sign`
 *   authHeaders     — object passed straight to axios
 *   prefillName     — string (from doc.your_approval.name)
 *   testIdPrefix    — 'vdr' | 'client'
 */
export default function SignModal({
  open, onClose, onSigned, signEndpoint, authHeaders,
  prefillName = '', testIdPrefix = 'vdr',
}) {
  const [name, setName] = useState(prefillName);
  const [title, setTitle] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Reset every time the modal opens (handles re-open after error or after a
  // previous user closed mid-flow).
  useEffect(() => {
    if (open) {
      setName(prefillName || '');
      setTitle('');
      setConsent(false);
      setErrMsg('');
      setSubmitting(false);
    }
  }, [open, prefillName]);

  // ESC closes the modal (unless submitting).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  const trimmedName = (name || '').trim();
  const previewFont = useMemo(() => pickFontForName(trimmedName), [trimmedName]);
  const rtl = isRTLFont(previewFont);

  const canSubmit = !!trimmedName && consent && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setErrMsg('');
    setSubmitting(true);
    try {
      await axios.post(
        signEndpoint,
        {
          signed_name: trimmedName,
          title: (title || '').trim() || null,
          consent: true,
          // Record the font the user actually saw in the preview — keeps the
          // backend audit row in sync with the rendered cursive. Tier-3 fonts
          // (Noto Devanagari / Naskh Arabic) write their real basename here
          // rather than always-cursive.
          font: backendFontName(previewFont),
        },
        { headers: authHeaders },
      );
      await onSigned?.();
      onClose?.();
    } catch (err) {
      setErrMsg(
        err?.response?.data?.detail
        || 'Could not save your signature. Please retry.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      data-testid={`${testIdPrefix}-sign-modal`}
      onClick={(ev) => { if (ev.target === ev.currentTarget && !submitting) onClose?.(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg bg-[#0b1310] border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#059669]/15 border border-[#059669]/40 flex items-center justify-center">
              <PenLine className="w-4 h-4 text-[#059669]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Sign digitally</h3>
              <p className="text-[11px] text-gray-500">
                Typed name rendered as your signature — no drawing, no upload.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
            className="text-gray-400 hover:text-white disabled:opacity-40"
            data-testid={`${testIdPrefix}-sign-cancel-btn`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs text-gray-300 mb-1.5">
              Full name <span className="text-gray-500">(your signature, editable)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
              className="w-full bg-[#111827] border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#059669]"
              data-testid={`${testIdPrefix}-sign-name-input`}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-300 mb-1.5">
              Title <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. Director, CEO, General Partner"
              className="w-full bg-[#111827] border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#059669]"
              data-testid={`${testIdPrefix}-sign-title-input`}
            />
          </div>

          {/* Live cursive preview — uses the SAME 3-tier chain as the PDF. */}
          <div>
            <p className="text-[11px] text-gray-500 mb-1.5 uppercase tracking-wider">
              Live preview · this is how your signature will appear
            </p>
            <div
              className="bg-[#111827] border border-gray-700 rounded p-4 min-h-[88px] flex items-center justify-start"
              data-testid={`${testIdPrefix}-sign-preview`}
            >
              <span
                style={{
                  fontFamily: `"${previewFont}", serif`,
                  fontSize: '36px',
                  color: '#e2e8f0',
                  lineHeight: 1.1,
                  direction: rtl ? 'rtl' : 'ltr',
                  unicodeBidi: 'isolate',
                  width: '100%',
                  textAlign: rtl ? 'right' : 'left',
                }}
                data-testid={`${testIdPrefix}-sign-preview-text`}
                data-font={previewFont}
              >
                {trimmedName || ' '}
              </span>
            </div>
            <p className="text-[10px] text-gray-600 mt-1">
              Font: <span className="font-mono">{previewFont}</span>
            </p>
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#059669]"
              data-testid={`${testIdPrefix}-sign-consent-checkbox`}
            />
            <span className="text-xs text-gray-300 leading-relaxed">
              I confirm that the typed name above will serve as my electronic
              signature for this document and that it has the same legal effect
              as a handwritten signature.
            </span>
          </label>

          {errMsg && (
            <p
              className="text-xs text-red-400 inline-flex items-start gap-1.5"
              data-testid={`${testIdPrefix}-sign-error`}
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {errMsg}
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-800 flex justify-end gap-2 bg-black/30">
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
            className="px-3 py-2 text-xs text-gray-300 hover:text-white disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 bg-[#059669] hover:bg-[#00d96e] disabled:bg-gray-700 disabled:text-gray-500 text-black font-semibold rounded px-4 py-2 text-sm"
            data-testid={`${testIdPrefix}-sign-adopt-btn`}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            <ShieldCheck className="w-4 h-4" /> Adopt & Sign
          </button>
        </div>
      </form>
    </div>
  );
}
